import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IFeedback } from '@app/main/interfaces/feedback';
import { FilterService } from '@app/shared/components/cm-filter/filter.service';
import { TagService } from '@app/shared/components/tag/tag.service';
import { mixpanelEventTypes } from '@app/shared/enums/mixpanel.enum';
import { EPermissionKeys } from '@app/shared/enums/permission-keys';
import { IDropdownOption } from '@app/shared/interfaces/dropdown-option';
import { IMessages } from '@app/shared/interfaces/messages';
import { IQuery } from '@app/shared/interfaces/query';
import { IQueryParams } from '@app/shared/interfaces/queryParams';
import { ITag } from '@app/shared/interfaces/tag';
import { IUser, UserDTO, UserRoles } from '@app/shared/interfaces/user';
import { DataService } from '@app/shared/services/data/data.service';
import { MixpanelService } from '@app/shared/services/mixpanel/mixpanel.service';
import { BrowserLocalStorageService } from '@app/shared/services/utils/browserLocalStorage.service';
import { UtilService } from '@app/shared/services/utils/util.service';
import { TranslateService } from '@ngx-translate/core';
import { locale } from 'devextreme/localization';
import { compare, Operation } from 'fast-json-patch';
import { BehaviorSubject, catchError, lastValueFrom, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { IPaginatedResponse } from '../../manage-groups/models/manage-group';
import { LAST_SELECTED_ORG, USER_DATA } from '../constants/constants';
import { defaultUserLanguage, userAllowedLangs, userLanguages } from '../constants/user-const';

export enum MessageState {
  INBOX = 0,
  ARCHIVED = 1,
  TRASH = 2,
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _baseUrl = 'Users';
  private _refreshUserSubject = new Subject<void>();

  private _userLoginData$ = new BehaviorSubject<UserDTO>(null);
  private _availableUserProfiles = [];
  private _userName: string;

  public availableRootOrgs: OrgSelectObject[] = [];
  public loadingUsers$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public selectedOrg: OrgSelectObject | null = null;
  public userLoginData$: Observable<UserDTO> = this._userLoginData$.asObservable();

  public defaultQueryParams = {
    searchFields: 'profileId,orgName,fullName,email',
    searchValues: '',
    filter: '',
    sort: 'fullName',
    selections: '',
    doCount: false,
    pageSize: 50,
  };

  public updateQueryParameters$ = new BehaviorSubject<IQueryParams>(this.defaultQueryParams);

  public users$: Observable<IPaginatedResponse<IUser>> = this.updateQueryParameters$.pipe(
    tap((_) => this.loadingUsers$.next(true)),
    switchMap((queryParams: IQueryParams) => {
      return this._dataService.post(`${this._baseUrl}/search`, queryParams).pipe(
        tap((_) => this.loadingUsers$.next(false)),
        catchError(this.handleError)
      );
    })
  );

  public updateNotificationsQueryParameters$ = new BehaviorSubject<{ params: IQueryParams; state: number } | null>(
    null
  );

  public filteredNotifications$: Observable<IMessages[]> = this.updateNotificationsQueryParameters$.pipe(
    switchMap((data: { params: IQueryParams; state: number } | null) => {
      if (data) {
        const url = `${this._baseUrl}/notifications/${data.state}`;
        return this._dataService.get(`${url}${UtilService.fillUrl(data.params) || ''}`);
      }
      return of([]);
    })
  );

  readonly refreshUser$ = this._refreshUserSubject.asObservable();

  constructor(
    private _dataService: DataService,
    private _localStorageService: BrowserLocalStorageService,
    private _mixpanelService: MixpanelService,
    private _router: Router,
    private _tagService: TagService,
    private _translate: TranslateService,
    private _filterService: FilterService
  ) { }

  /**
   * This will retrieve the cmcloud user data.
   * It is called just after login, and when you add a new customer org to the current account.
   */
  public initCmCloudUser(noCache: boolean = false): void {
    const url = `${this._baseUrl}/v2/login`;

    // initialize the storage
    this._localStorageService
      .initializeStorage()
      .pipe(
        switchMap(() => {
          if (noCache) {
            return this._dataService.post(url);
          } else {
            // Check if user data is already persisted in session storage
            const storedUserData = this._localStorageService.getItem(USER_DATA);
            if (storedUserData) {
              let userData = storedUserData;
              if (typeof storedUserData === 'string') {
                try {
                  userData = JSON.parse(storedUserData);
                } catch (e) {
                  return this._dataService.post(url);
                }
              }
              if (userData && userData[0] && userData[0].email === this._userName) {
                // continue loading user settings and complete the observable with no further actions
                this._initUserOrgs(userData);
                return of();
              } else {
                return this._dataService.post(url);
              }
            } else {
              return this._dataService.post(url);
            }
          }
        })
      )
      .subscribe({
        next: (data) => {
          if (data) {
            this._initUserOrgs(data);
            this._localStorageService.setItem(USER_DATA, data);
          }
        },
        error: (error) => {
          console.error('Error fetching user data: ', error);
        },
      });
  }

  public clearUserDataCache(): void {
    this._localStorageService.removeItem(USER_DATA);
  }

  public setActiveUserAccount(userName: string): void {
    this._userName = userName;
  }

  public changeRootOrg(selectedOrg: string) {
    this.availableRootOrgs.forEach((org: OrgSelectObject) => {
      if (org.orgId === selectedOrg) {
        this.selectedOrg = org;
      }
    });

    this._availableUserProfiles.forEach((userData) => {
      if (userData.orgId === selectedOrg) {
        this._filterService.cleanAppliedFilters();
        this._loadUserLoginData(userData);
        this._localStorageService.setItem(LAST_SELECTED_ORG, selectedOrg);
        this._router.navigate(['/']);
      }
    });
  }

  public getUserLoginData(): UserDTO | null {
    return this._userLoginData$.getValue();
  }

  getUsersWithSearch(query): Observable<any> {
    if (query.selections) {
      query.selections = JSON.stringify(query.selections);
    }

    const url = 'Users/search';
    return this._dataService.post(url, query);
  }

  getUsersByOrganization = (orgId: string): Observable<any> => {
    const orgIdStringify = JSON.stringify({ orgId });
    const url = `Users?Selections=${orgIdStringify}`;
    return this._dataService.get(url);
  };

  /**
   * Function to get all users grouped by role
   */
  public getAllUsersByGroup(params: any): Observable<Response> {
    let url = `${this._baseUrl}/group/search`;
    url += `?groupId=${params.groupId}`;
    url += `&groupName=${params.groupName}`;
    url += `&groupArray=${params.groupArray}`;

    delete params.groupId;
    delete params.groupName;
    delete params.groupArray;

    return this._dataService.post(url, params);
  }

  /**
   * Function to get all users by role id
   */
  public getAllUsersByGroupId(params: any): Promise<IUser[]> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const configUrl = UtilService.fillUrl(params);
      const url = this._baseUrl + (configUrl ? configUrl : '');
      const user = await this._dataService.get(url).toPromise();
      resolve(user.result);
    });
  }

  /**
   * Function to get a user by id
   */
  public getUserById(id: string): Promise<IUser> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const url = `${this._baseUrl}/${id}`;
      const user = await this._dataService.get(url).toPromise();
      resolve(user);
    });
  }

  /**
   * Function to save a new user
   */
  saveUser(user: IUser): Observable<void> {
    const url = this._baseUrl;
    return this._dataService.post(url, user).pipe(
      tap(() => {
        this._mixpanelService.trackEvent(mixpanelEventTypes.USER_REGISTERED, {
          newFirstName: user.firstName,
          newLastName: user.lastName,
          newEmail: user.email,
          newUserId: user._id,
        });
        this._refreshUserSubject.next();
      })
    );
  }

  getItem(key: string) {
    return this._localStorageService.getItem(key);
  }

  setItem(key: string, value: any) {
    return this._localStorageService.setItem(key, value);
  }

  getSessionOrgId(key: string) {
    return this.getItem(key);
  }

  setSessionOrgId(key: string, value: any) {
    return this.setItem(key, value);
  }

  getUserDBPreferences(key: string) {
    return this.getItem(key);
  }

  setUserDBPreferences(key: string, value: any) {
    return this.setItem(key, value);
  }

  /**
   * Function to update a user
   */
  public async updateUser(user: IUser, editedUser: IUser): Promise<Observable<any>> {
    const url = `${this._baseUrl}/${editedUser._id}`;
    const patch = await this._processUserPatch(user, editedUser);

    return this._dataService.patch(url, patch).pipe(
      tap(() => {
        this._refreshUserSubject.next();
      })
    );
  }

  /**
   * Function to get Crib Access List by user id
   */
  public getUserAccessList(userId: string): Promise<any> {
    const url = `${this._baseUrl}/${userId}/cribaccess`;
    return this._dataService.get(url).toPromise();
  }

  /**
   * Function to remove a tag from user
   */
  public removeTagFromUser(tag: ITag, user: IUser): Promise<boolean> {
    return this._tagService.deleteByCollection(tag._id, 'user', user._id).toPromise();
  }

  /**
   * Function to reactivate or deactivate a user
   */
  public toggleUserActivation(user: IUser, deactivate: boolean): Observable<any> {
    const url = `${this._baseUrl}/${user._id}`;
    const userActivateOp = Object.assign({}, user);
    userActivateOp.inactive = deactivate;
    const patch = compare(user, userActivateOp);
    return this._dataService.patch(url, patch);
  }

  public getAdminUsers(pageIndex?: number): Promise<IUser> {
    const fields =
      // eslint-disable-next-line max-len
      'firstName,lastName,inviteStatus,imageUrl,email&filter="roles.name":"Admin","inactive":{"$ne":true}&Sort=%2BfirstName';

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const url = `${this._baseUrl}?fields=${fields}&pageIndex=${pageIndex}`;

      const user = await this._dataService.get(url).toPromise();
      resolve(user);
    });
  }

  // TODO: this should be on the message service
  public getMessages(userId: string, state?: number, query?: IQuery): Promise<IMessages[]> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      if (!query) {
        query = { sort: '-createdDate' };
      } else if (!query.sort) {
        query.sort = '-createdDate';
      }

      let url = `${this._baseUrl}/${userId}/messages${state.toString() ? `/${state}` : ''}`;
      url = `${url}/${UtilService.fillUrl(query)}`;

      const user = await this._dataService.get(url).toPromise();
      resolve(user);
    });
  }

  public getUsersCount(): Observable<any> {
    const url = 'users/count';
    return this._dataService.get(url);
  }

  public getStaffUsersCount(): Observable<any> {
    const url = "users/count?filter='roles.name':'Access Only'";
    return this._dataService.get(url);
  }

  public getUsersByOrgId(orgId: string): Observable<any> {
    const orgParam = orgId ? `&orgId=${orgId}` : '';
    const url = `${this._baseUrl}/filteredbyorg?Fields=firstName,lastName,_id${orgParam}`;
    return this._dataService.get(url);
  }

  private handleError(): Observable<any> {
    //TODO add error handling
    return of({});
  }

  get usersLanguages(): Observable<any> {
    return of(this.sortArrayByObjectPropAsc(userLanguages, 'label'));
  }

  private sortArrayByObjectPropAsc(array: IDropdownOption[], propName: String) {
    return array.sort((a, b) => {
      return a[`${propName}`] > b[`${propName}`] ? 1 : -1;
    });
  }

  public submitFeedback(feedback: IFeedback): Observable<any> {
    const url = `${this._baseUrl}/userfeedback`;
    return this._dataService.post(url, feedback);
  }

  public getUserCostCenters(orgId): Observable<any> {
    const url = `costcenters/UserCostCenters?orgId=${orgId}&filter="inactive":{"$ne":true}&sort=name`;
    return this._dataService.get(url).pipe(catchError(() => of([])));
  }

  public getInboxMessages(query: IQuery = {}): Observable<any> {
    if (!query.filter) {
      query.filter = `'state':${MessageState.INBOX}`;
    }

    return this._dataService.get(`${this._baseUrl}/notifications${UtilService.fillUrl(query) || ''}`);
  }

  public getUnreadMessages(query: IQuery = {}): Observable<any> {
    if (!query.filter) {
      query.filter = `'state':${MessageState.INBOX},'readDate':{ $exists: false }`;
    }

    return this._dataService.get(`${this._baseUrl}/notifications${UtilService.fillUrl(query) || ''}`);
  }

  public getUnreadMessagesCount(query: IQuery = {}): Observable<any> {
    if (!query.filter) {
      query.filter = `'state':${MessageState.INBOX},'readDate':{ $exists: false }`;
    }

    return this._dataService.get(`${this._baseUrl}/notifications/${UtilService.fillUrl(query) || ''}`);
  }

  public getMessageById(id: string): Promise<IMessages> {
    const url = `${this._baseUrl}/notifications/${id}`;
    return lastValueFrom(this._dataService.get(url));
  }

  public markAllAsRead(): Observable<any> {
    const url = `${this._baseUrl}/notifications/MarkAllAsRead`;
    return this._dataService.post(url);
  }

  public updateMessageReadDate(messagesIds: string[]): Promise<any> {
    const url = `${this._baseUrl}/notifications/batch/update/ReadDate/now()`;
    return lastValueFrom(this._dataService.post(url, messagesIds));
  }

  public updateMessageState(messagesIds: string[], state: number): Promise<any> {
    const url = `${this._baseUrl}/notifications/batch/update/state/${state}`;
    return lastValueFrom(this._dataService.post(url, messagesIds));
  }

  public deleteMessage(messagesIds: string[]): Promise<any> {
    const url = `${this._baseUrl}/notifications/batch/delete`;
    return lastValueFrom(this._dataService.post(url, messagesIds));
  }

  public deleteOneMessage(messagesId: string): Promise<any> {
    const url = `${this._baseUrl}/notifications/${messagesId}`;
    return lastValueFrom(this._dataService.delete(url));
  }

  public flagMessage(messagesIds: string[], state: number): Promise<any> {
    const url = `${this._baseUrl}/notifications/batch/update/flag/${state}`;
    return lastValueFrom(this._dataService.post(url, messagesIds));
  }

  public getAlertsList(): Observable<any> {
    const query = { sort: 'name' };
    const url = `${this._baseUrl}/alerts${UtilService.fillUrl(query)}`;
    return this._dataService.get(url);
  }

  public updateAlert(id: string, patch: any): Observable<Response> {
    return this._dataService.patch(`${this._baseUrl}/alerts/${id}`, patch);
  }

  public updateAlertMethod(id: string, method: string[]): Observable<Response> {
    return this._dataService.putWithId(`${this._baseUrl}/alerts/${id}/actions/${method.toString()}`, null);
  }

  private _initUserOrgs(data: UserDTO[]): void {
    // if the user email belongs to more than one org, we will have multiple UserDTO objects on the response from the login endpoint.
    if (data.length > 1) {
      // store all the available profiles
      this._availableUserProfiles = data;

      // populate the org selector dropdown
      this.availableRootOrgs = [];
      data.forEach((userObject: UserDTO) => {
        const org = {
          orgId: userObject.orgId,
          orgName: userObject.orgName,
        };
        this.availableRootOrgs.push(org);
      });

      // check local storage for persisted selected org
      let loadingPersistedOrg = false;
      const storedSelectedOrgId = this._localStorageService.getItem(LAST_SELECTED_ORG);
      if (storedSelectedOrgId) {
        this._availableUserProfiles.forEach((userProfile: UserDTO) => {
          if (userProfile.orgId === storedSelectedOrgId) {
            loadingPersistedOrg = true;
            this.selectedOrg = {
              orgId: userProfile.orgId,
              orgName: userProfile.orgName,
            };
            this._loadUserLoginData(userProfile);
          }
        });
      }

      // if no persisted org from previous session,
      // load first profile as default
      if (!loadingPersistedOrg) {
        this.selectedOrg = {
          orgId: data[0].orgId,
          orgName: data[0].orgName,
        };
        this._loadUserLoginData(data[0]);
      }
    } else {
      // user has only one profile, load the default one.
      this._loadUserLoginData(data[0]);
    }
  }

  private _loadUserLoginData(userLoginData: any) {
    if (!userLoginData) return;

    const defaultLang = userLoginData.defaultLanguage ?? defaultUserLanguage;
    const [lang, country] = defaultLang.split('-');
    const userLang = userAllowedLangs.includes(lang) ? lang : defaultUserLanguage.split('-')[0];
    this._translate.use(userLang);

    // set the langugage to be used on the devextreme components (grid included)
    locale(userLang);

    const userDTO: UserDTO = {
      defaultLanguage: userLoginData.defaultLanguage ?? defaultLang,
      email: userLoginData.email,
      fullName: userLoginData.fullName,
      imageUrl: userLoginData.imageUrl,
      orgAccess: userLoginData.orgAccess ?? [],
      orgId: userLoginData.orgId,
      orgName: userLoginData.orgName,
      permissions: userLoginData.permissions ?? [],
      orgPermissions: userLoginData.orgPermissions ?? [],
      profileId: userLoginData.profileId,
      roles: userLoginData.roles?.map((role) => role.name),
      rootOrgId: userLoginData.rootOrgId,
      userId: userLoginData._id,
      isDistributor: userLoginData.orgPermissions.some((p) => p === EPermissionKeys.CUSTOMER_VIEW),
      isAdmin: userLoginData.roles?.map((role) => role.name)?.some((role) => role === UserRoles.Admin),
      isOwner: userLoginData.roles?.map((role) => role.name)?.some((role) => role === UserRoles.Owner),
    };

    if (userDTO.userId && userDTO.orgId) {
      this._userLoginData$.next(userDTO);
    }
  }

  private async _processUserPatch(user: IUser, editedUser: IUser): Promise<Operation[]> {
    let patch: Operation[] = compare(user, editedUser);

    // Handle the case where the vending pin has been changed
    const vendingPinOp = patch.find((op) => op.path === '/vendingPin' && op.op === 'replace');
    if (vendingPinOp) {
      patch = patch.filter((i) => !i.path.startsWith('/vendingPin'));
      patch.push({
        op: 'replace',
        path: '/vendingPin',
        value: await UtilService.hashValue(editedUser.vendingPin),
      });
    }

    // Handle the case where the roles have been changed
    const hasRoleModification = patch.some((op) => op.path.startsWith('/roles'));
    if (hasRoleModification) {
      patch = patch.filter((op) => !op.path.startsWith('/roles'));
      patch.push({
        op: 'replace',
        path: '/roles',
        value: JSON.parse(JSON.stringify(editedUser.roles)),
      });
    }

    // Handle the case where the tags have been changed
    if (editedUser.tags && patch.some((op) => op.path.startsWith('/tags'))) {
      patch = patch.filter((op) => !op.path.startsWith('/tags'));
      patch.push({
        op: 'replace',
        path: '/tags',
        value: JSON.parse(JSON.stringify(editedUser.tags)),
      });
    }

    // Removes operations that are not expected
    patch = patch.filter(
      ({ path }) =>
        !(
          path === '/supervisor' ||
          path.startsWith('/supervisor/') ||
          path === '/phone' ||
          path === '/countryCode' ||
          path === '/_id' ||
          path === '/idPrefix'
        )
    );

    // Replace 'remove' operations with 'replace'
    patch = patch.map((operation) => ({
      ...operation,
      op: operation.op === 'remove' ? 'replace' : operation.op,
    })) as Operation[];

    return patch;
  }
}

export interface OrgSelectObject {
  orgId: string;
  orgName: string;
}
